/* eslint-disable @typescript-eslint/no-explicit-any */

import { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bars3Icon } from '@heroicons/react/24/solid';
import useUserAccount from '../../hooks/userAccountHook';
import useRoute from '../../hooks/routesHook';
import Notifications from '../notification/Notification';
import clsx from 'clsx';
import NotificationIcon from '../notification/NotificationIcon';
import { NOTIFICATION_TYPE } from '../../resources/enums';
import ReportFeatureBug from '../reportIssue';
import ReportIcon from '../reportIssue/ReportIcon';
import { getCountriesFromState, getSelectedCountry, SET_SELECTED_COUNTRY, UPDATE_COUNTRIES_IN_STATE } from '../../states/slices/resourceSlice';
import { useSelector } from 'react-redux/es/hooks/useSelector';
import { useDispatch } from 'react-redux/es/hooks/useDispatch';
import SelectDropdownWithImage from '../form-control/SelectDropdownWithImage';
import { getAllOperationalCountries } from '../../api/requests';
import { ICountry } from '../../resources/interfaces';
import { useQuery } from '@tanstack/react-query';
import useModal from '../../hooks/modalHook';
import { Drawer } from '../elements/Drawer';
import useLayoutHook from '../../hooks/layoutHook';
import TrialCountdownPill from '../ui/TrialCountdownPill';
import { getCompanyOnboardingStatusFromState } from '../../states/slices/globalSlice';
import { getTrialEndDaysFromDate, showToast } from '../../utils/miscFunctions';
import SubscribedPlanIndication from '../ui/SubscribedPlanIndication';
import ThemeToggle from '../ThemeToggle';
import FontSizeToggle from '../FontSizeToggle';
import { FloatingCalculatorButton } from '../../views/admin/quotes/calculators/widgetChecker/components/FloatingCalculatorButton';
import WorkspaceSetupBannerMinimized from '../ui/WorkspaceSetupBannerMinimized';

// Lazy load the floating calculator button
// const LazyFloatingCalculator = lazy(() =>
//   import('../../views/admin/quotes/calculators/widgetChecker/components/FloatingCalculatorButton')
//     .then(module => ({ default: module.FloatingCalculatorButton }))
// );

interface INavbarProps {
  handleMenuClick: (e: any) => void;
  isSticky?: boolean;
  hideProfileSection?: boolean;
  hideMobileMenu?: boolean;
}

function Navbar(props: INavbarProps) {
  const { signOut, user } = useUserAccount();
  const { getDashboardBase } = useRoute();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const country = useSelector(getSelectedCountry);
  const countriesInState = useSelector(getCountriesFromState);
  const [countries, setCountries] = useState<ICountry[]>(countriesInState);
  const [selectedCountry, setSelectedCountry] = useState<string>(country?.name || 'Ghana');
  const [allNotifications,] = useState<any[]>([]);
  const [myNotifications, setMyNotifications] = useState<any[]>([]);
  const { ModalComponent, updateModalStates, closeModal } = useModal()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { isMobile, isTablet, isDesktop } = useLayoutHook()
  const { isSticky = true } = props;
  const onboardingStatus = useSelector(getCompanyOnboardingStatusFromState);

  const handleOnline = () => {
    if (!('Notification' in window)) {
      console.log('This browser does not support desktop notification');
      return;
    }

    if (Notification.permission === 'granted') {
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission().then(() => { });
    }
  };

  const getCountries = () => {
    getAllOperationalCountries()
      .then((response: any) => {
        setCountries(response);
        dispatch(UPDATE_COUNTRIES_IN_STATE(response))
      })
      .catch((error: any) => { })
      .finally(() => {
      });
  };

  // useSSE<any>({
  //   endpoint: `/sse/admin/notifications/me/${user.id}`,
  //   eventType: 'my-notifications',
  //   onData: (data) => {
  //     setMyNotifications((prevNotifications) => {
  //       if (JSON.stringify(prevNotifications) !== JSON.stringify(data)) {
  //         showNotification(data[data.length - 1]);
  //         return data;
  //       }
  //       return prevNotifications;
  //     });
  //   }
  // });

  const showNotification = (notification: any) => {
    const { message, data } = notification;
    const notificationTitle = 'New Notification';

    // Display desktop notification...
    if ('Notification' in window) {
      if (Notification.permission === 'granted') {
        const notification = new Notification(notificationTitle, { body: message });
        setTimeout(() => {
          notification.close();
        }, 10 * 1000);
        notification.addEventListener('click', () => {
          const route = `${getDashboardBase()}/${handleNotificationClick(data)}`;
          navigate(route);
        });
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then(() => {
          // Handle permission...
        });
      }
    }
  };

  const handleCreateBugReport = () => {
    updateModalStates({
      show: true,
      onClose: () => {
        closeModal()
      },
      size: "3xl",
      children: <ReportFeatureBug
        onSuccess={() => {
          closeModal();
          showToast.success('Report submitted successfully');
        }}
      />,
      title: 'Support Request'

    })
  }

  const handleNotificationClick = (notification: any) => {
    let route = '';
    switch (notification.type) {
      case NOTIFICATION_TYPE.POLICY:
        route = `policies/${notification.identifier}`;
        break;
      case NOTIFICATION_TYPE.LEAD:
        route = `leads/${notification.identifier}`;
        break;
      case NOTIFICATION_TYPE.CUSTOMER:
        route = `customers/${notification.identifier}`;
        break;
      case NOTIFICATION_TYPE.QUOTE:
        route = `quotes/${notification.identifier}`;
        break;

      case NOTIFICATION_TYPE.PAYMENT:
        route = `policies/${notification.identifier}`;
        break;

      case NOTIFICATION_TYPE.TASK:
        route = `policies/${notification.identifier}`;
        break;

      default:
        break;
    }

    navigate(`${getDashboardBase()}/${route}`, { replace: true });
  };

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  let notificationsLength = myNotifications?.length;

  useEffect(() => {
    handleOnline();
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  useEffect(() => {
    if (selectedCountry && countries?.length > 0) {
      const selectedCountryItem = countries?.find(c => c.name == selectedCountry);
      if (selectedCountryItem) {
        dispatch(SET_SELECTED_COUNTRY(selectedCountryItem));
      }
    }
  }, [selectedCountry, countries, dispatch]);

  useQuery({
    queryKey: ['getCountries'],
    queryFn: getCountries,
  })

  return (
    <div className={clsx('py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 w-full shadow-sm', { ['sticky top-0 z-[10]']: isSticky })}>
      <div className="max-w-screen-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4">
          {/* Mobile menu button */}
          <div className="flex items-center">
            <div className="xl:hidden">
              {!(props.hideMobileMenu || user.isFuelVendor()) && (
                <button
                  onClick={props?.handleMenuClick}
                  className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  aria-label="Open menu"
                >
                  <Bars3Icon className="w-6 h-6 text-gray-900 dark:text-white" />
                </button>
              )}
            </div>
          </div>

          {/* Right section with actions */}
          {!props.hideProfileSection && (
            <div className="flex items-center gap-3 ml-auto">

              {/* Country selector for internal admins */}
              {countries && user.isInternalAdmin() && (
                <div className="hidden lg:block">
                  <SelectDropdownWithImage
                    list={countries.map((country: ICountry) => ({
                      name: country?.name ?? 'Ghana',
                      imageSrc: country?.flag,
                      value: country?.name ?? 'Ghana'
                    }))}
                    listSelectedValue={country?.name}
                    onChange={(option: string) => {
                      setSelectedCountry(option)
                    }}
                    showDropdownIcon={false}
                  />
                </div>
              )}

              {/* Action buttons */}
              <div className="flex items-center gap-4">
                {/* <Suspense fallback={<div className="w-8 h-8" />}> */}
                <FloatingCalculatorButton />
                {/* </Suspense> */}

                <ThemeToggle />

                <FontSizeToggle />

                <ReportIcon onClick={handleCreateBugReport} />
              </div>

              {/* Workspace setup banner - prominent placement */}
              {onboardingStatus?.onboardingComplete === false && user?.isBroker() && (
                <div className="hidden md:block">
                  <WorkspaceSetupBannerMinimized />
                </div>
              )}


              {/* {(allNotifications || myNotifications) && (
                <NotificationIcon notifications={notificationsLength} onClick={toggleDrawer} />
              )} */}

              {/* <Drawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} position="right" size={isMobile ? 'full' : 600} height={isMobile ? 'full' : 'auto'}>
                <Notifications
                  handleOnclick={handleNotificationClick}
                  allNotifications={allNotifications}
                  meNotifications={myNotifications}
                />
              </Drawer> */}

            </div>
          )}
        </div>
      </div>

      {/* Mobile workspace setup banner */}
      {onboardingStatus?.onboardingComplete === false && user?.isBroker() && (
        <div className="md:hidden px-4 sm:px-6 lg:px-8 pt-3">
          <WorkspaceSetupBannerMinimized />
        </div>
      )}

      <ModalComponent />
    </div>
  );
}

export default Navbar;
